import { Box, Chip, Tooltip, Typography } from '@mui/material'
import { StepUpProvider } from 'pbts/c1/api/stepup/v1/stepup'
import { FC } from 'react'
import { FormattedMessage } from 'react-intl'

/**
 * Props for the StepUpProviderDisplay component
 */
export interface StepUpProviderDisplayProps {
    /**
     * The Step Up Provider to display
     */
    provider: StepUpProvider
    /**
     * Whether to show the provider ID
     */
    showId?: boolean
}

/**
 * Displays the type of a Step Up Provider with appropriate styling
 */
const StepUpProviderTypeChip: FC<{ provider: StepUpProvider }> = ({ provider }) => {
    let label = <FormattedMessage id="component_step_up_provider_type_unknown" defaultMessage="Unknown" />

    if (provider.settings.oneofKind === 'oauth2') {
        label = <FormattedMessage id="component_step_up_provider_type_oauth2" defaultMessage="OAuth 2.0" />
    } else if (provider.settings.oneofKind === 'microsoft') {
        label = <FormattedMessage id="component_step_up_provider_type_microsoft" defaultMessage="Microsoft" />
    }

    return <Chip size="small" color="primary" label={label} />
}

/**
 * Displays information about a Step Up Provider in a compact form
 */
export const StepUpProviderDisplay: FC<StepUpProviderDisplayProps> = ({
    provider,
    showId = false,
}) => {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StepUpProviderTypeChip provider={provider} />

            <Tooltip title={provider.displayName}>
                <Typography noWrap sx={{ maxWidth: '150px' }}>
                    {provider.displayName}
                </Typography>
            </Tooltip>

            {showId && (
                <Tooltip title={provider.id}>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: '100px' }}>
                        (
                        {provider.id}
                        )
                    </Typography>
                </Tooltip>
            )}

            {!provider.enabled && (
                <Chip
                    size="small"
                    color="error"
                    label={<FormattedMessage id="component_step_up_provider_disabled" defaultMessage="Disabled" />}
                />
            )}
        </Box>
    )
}
